// NetTV Theme context - backward compatible wallpaper picker + dark/light mode
import React, { createContext, useCallback, useContext, useEffect, useState } from "react";

const ThemeCtx = createContext(null);

export const DEFAULT_WALLPAPERS = [
  { id: "none", name: "None",        url: null },
  { id: "a",    name: "Wallpaper 1", url: "/wallpapers/wallpaper-a.png" },
  { id: "b",    name: "Wallpaper 2", url: "/wallpapers/wallpaper-b.png" },
  { id: "c",    name: "Wallpaper 3", url: "/wallpapers/wallpaper-c.png" },
  { id: "d",    name: "Wallpaper 4", url: "/wallpapers/wallpaper-d.png" },
];

export function ThemeProvider({ children }) {
  const [mode, setMode] = useState(() => {
    try { return localStorage.getItem("nettv.mode") || "dark"; }
    catch { return "dark"; }
  });
  const [wallpaper, setWallpaperState] = useState(() => {
    try { return localStorage.getItem("nettv.wallpaper") || null; }
    catch { return null; }
  });
  const [customWallpapers, setCustomWallpapers] = useState(() => {
    try { return JSON.parse(localStorage.getItem("nettv.customWallpapers") || "[]"); }
    catch { return []; }
  });

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", mode);
    try { localStorage.setItem("nettv.mode", mode); } catch {}
  }, [mode]);

  useEffect(() => {
    const root = document.documentElement;
    if (wallpaper) {
      root.style.setProperty("--nettv-wallpaper", `url(${JSON.stringify(wallpaper)})`);
      document.body.classList.add("has-wallpaper");
      try { localStorage.setItem("nettv.wallpaper", wallpaper); } catch {}
    } else {
      root.style.removeProperty("--nettv-wallpaper");
      document.body.classList.remove("has-wallpaper");
      try { localStorage.removeItem("nettv.wallpaper"); } catch {}
    }
  }, [wallpaper]);

  const setWallpaper = useCallback((url) => setWallpaperState(url || null), []);

  const addCustomWallpaper = useCallback((url, name = "Custom") => {
    const item = { id: `custom-${Date.now()}`, name, url, custom: true };
    setCustomWallpapers((prev) => {
      const next = [...prev, item];
      try { localStorage.setItem("nettv.customWallpapers", JSON.stringify(next)); } catch {}
      return next;
    });
    return item;
  }, []);

  const removeCustomWallpaper = useCallback((id) => {
    setCustomWallpapers((prev) => {
      const next = prev.filter((w) => w.id !== id);
      try { localStorage.setItem("nettv.customWallpapers", JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);

  const toggleMode = useCallback(
    () => setMode((m) => (m === "dark" ? "light" : "dark")),
    [],
  );

  const value = {
    mode, setMode, toggleMode,
    wallpaper, setWallpaper,
    wallpapers: [...DEFAULT_WALLPAPERS, ...customWallpapers],
    addCustomWallpaper, removeCustomWallpaper,
  };
  return <ThemeCtx.Provider value={value}>{children}</ThemeCtx.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeCtx);
  if (!ctx) throw new Error("useTheme must be used inside <ThemeProvider>");
  return ctx;
}
